import React from 'react';
import { List, Datagrid, TextField, NumberField, DateField, useListContext, Link } from 'react-admin';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import useSupplyStyles from '../controls/SupplyStyles';
import ErrorBoundary from '../ErrorBoundary';

/**
 * Which supply type / vendor these lots belong to, as a horizontal strip
 * above the results rather than a card down the left. Every lot in the list
 * shares these values (the list is always filtered to one supply type), so
 * the first row is representative.
 */
const LotHeader = () => {
    const classes = useSupplyStyles();
    const { data, ids } = useListContext();
    const first = ids && ids.length && data ? data[ids[0]] : null;

    const facts = [
        { label: 'Supply Type', value: first ? first.supply_type : '-' },
        { label: 'Vendor', value: first && first.vendor ? first.vendor : '-' },
        { label: 'Category', value: first && first.category ? first.category : '-' },
    ];

    return (
        <Box className={classes.headerStrip}>
            {facts.map(fact => (
                <Box key={fact.label} className={classes.headerFact}>
                    <Typography className={classes.asideLabel}>{fact.label}:</Typography>
                    <Typography className={classes.headerValue}>{fact.value}</Typography>
                </Box>
            ))}
        </Box>
    );
};

const FoundHeader = () => {
    const classes = useSupplyStyles();
    const { total } = useListContext();
    return (
        <Typography className={classes.sectionTitle}>
            <span style={{marginLeft: 16 }}>Supply Inventory ({total || 0})</span>
        </Typography>
    );
};

/** Lot # opens the receipt detail. */
const LotLinkField = ({ record }) =>
    record ? <Link to={`/supplyreceipts/${record.id}/show`}>{record.lot_no}</Link> : null;

LotLinkField.defaultProps = { label: 'Lot #', source: 'lot_no' };

const LotContent = props => {
    const rowStyle = record => {
        if (!record) { return {}; }
        if (record.days_to_expire < 0) { return { backgroundColor: '#ffe8e8' }; }
        if (record.days_to_expire <= (record.expiry_warn_days || 30)) {
            return { backgroundColor: '#fff6e0' };
        }
        return {};
    };

    return (
        <React.Fragment>
            <LotHeader />
            <FoundHeader />
            <Datagrid {...props} rowStyle={rowStyle}>
                <LotLinkField />
                <DateField source="receipt_date" label="Receipt Date" />
                <DateField source="expiry_date" label="Expiry Date" />
                <NumberField source="qty_qa_pending" label="QA Pending" />
                <NumberField source="qty_reserve" label="Reserve" />
                <NumberField source="qty_release" label="Release" />
                <NumberField source="qty_rel_sublot" label="Release Sublot" />
                <NumberField source="qty_transfer" label="Transfer" />
                <NumberField source="qty_hold" label="Hold" />
                <NumberField source="qty_total" label="Total" />
                <TextField source="vendor" label="Vendor" />
            </Datagrid>
        </React.Fragment>
    );
};

// No `aside` here: the supply type / vendor summary is a horizontal strip at
// the top of the results (see LotContent) rather than a left-hand card.
const SupplyLotList = props => (
    <ErrorBoundary>
        <List
            {...props}
            title="Supply Inventory"
            perPage={25}
            sort={{ field: 'expiry_date', order: 'ASC' }}
            bulkActionButtons={false}
            empty={false}
            exporter={false}
        >
            <LotContent />
        </List>
    </ErrorBoundary>
);

export default SupplyLotList;
